import React, { useState, useMemo, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { searchFoods, addRecipe, getRecipes, updateRecipe, deleteRecipe } from '../../services/database';
import { FaUtensils, FaSearch, FaPlus, FaMinus, FaTrash, FaCamera, FaSave, FaShare, FaBook, FaEdit, FaTimes } from 'react-icons/fa';
import './RecipeBuilder.css';

const RecipeBuilder = () => {
  const { currentUser, userProfile } = useAuth();

  // Recipe library state
  const [recipes, setRecipes] = useState([]);
  const [viewMode, setViewMode] = useState('builder'); // 'builder' or 'library'
  const [editingRecipe, setEditingRecipe] = useState(null);

  // Recipe builder state
  const [recipeName, setRecipeName] = useState('');
  const [recipeDescription, setRecipeDescription] = useState('');
  const [servings, setServings] = useState(4);
  const [prepTime, setPrepTime] = useState('');
  const [cookTime, setCookTime] = useState('');
  const [category, setCategory] = useState('');
  const [dietaryTags, setDietaryTags] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [instructions, setInstructions] = useState(['']);
  const [recipePhoto, setRecipePhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);

  // Food search state
  const [foodSearchTerm, setFoodSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // Load recipes on mount
  useEffect(() => {
    loadRecipes();
  }, [currentUser]);

  const loadRecipes = async () => {
    if (!currentUser) return;
    try {
      const userRecipes = await getRecipes(currentUser.uid);
      setRecipes(userRecipes);
    } catch (error) {
      console.error('Error loading recipes:', error);
    }
  };

  // Food search
  useEffect(() => {
    const searchTimeout = setTimeout(async () => {
      if (foodSearchTerm.trim().length >= 2) {
        setIsSearching(true);
        try {
          const results = await searchFoods(foodSearchTerm);
          setSearchResults(results);
        } catch (error) {
          console.error('Error searching foods:', error);
          setSearchResults([]);
        } finally {
          setIsSearching(false);
        }
      } else {
        setSearchResults([]);
      }
    }, 300);

    return () => clearTimeout(searchTimeout);
  }, [foodSearchTerm]);

  // Add ingredient to recipe
  const addIngredient = (food) => {
    const newIngredient = {
      id: Date.now(),
      foodId: food.id,
      name: food.name,
      brand: food.brand,
      quantity: 1,
      unit: food.servingUnit || 'serving',
      calories: food.calories,
      protein: food.protein,
      carbs: food.carbs,
      fat: food.fat
    };
    setIngredients([...ingredients, newIngredient]);
    setFoodSearchTerm('');
    setSearchResults([]);
  };

  // Update ingredient quantity
  const updateIngredientQuantity = (ingredientId, newQuantity) => {
    if (newQuantity <= 0) return;
    setIngredients(ingredients.map(ing =>
      ing.id === ingredientId ? { ...ing, quantity: newQuantity } : ing
    ));
  };

  // Remove ingredient
  const removeIngredient = (ingredientId) => {
    setIngredients(ingredients.filter(ing => ing.id !== ingredientId));
  };

  // Calculate total nutrition
  const totalNutrition = useMemo(() => {
    return ingredients.reduce((totals, ingredient) => ({
      calories: totals.calories + (ingredient.calories * ingredient.quantity),
      protein: totals.protein + (ingredient.protein * ingredient.quantity),
      carbs: totals.carbs + (ingredient.carbs * ingredient.quantity),
      fat: totals.fat + (ingredient.fat * ingredient.quantity)
    }), { calories: 0, protein: 0, carbs: 0, fat: 0 });
  }, [ingredients]);

  // Calculate per serving nutrition
  const perServingNutrition = useMemo(() => {
    if (servings === 0) return totalNutrition;
    return {
      calories: Math.round(totalNutrition.calories / servings),
      protein: Math.round(totalNutrition.protein / servings),
      carbs: Math.round(totalNutrition.carbs / servings),
      fat: Math.round(totalNutrition.fat / servings)
    };
  }, [totalNutrition, servings]);

  // Add instruction step
  const addInstructionStep = () => {
    setInstructions([...instructions, '']);
  };

  // Update instruction step
  const updateInstruction = (index, value) => {
    const newInstructions = [...instructions];
    newInstructions[index] = value;
    setInstructions(newInstructions);
  };

  // Remove instruction step
  const removeInstruction = (index) => {
    if (instructions.length === 1) return;
    setInstructions(instructions.filter((_, i) => i !== index));
  };

  // Toggle dietary tag
  const toggleDietaryTag = (tag) => {
    if (dietaryTags.includes(tag)) {
      setDietaryTags(dietaryTags.filter(t => t !== tag));
    } else {
      setDietaryTags([...dietaryTags, tag]);
    }
  };

  // Handle photo upload
  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setRecipePhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Save recipe
  const saveRecipe = async () => {
    if (!recipeName.trim()) {
      alert('Please enter a recipe name');
      return;
    }

    if (ingredients.length === 0) {
      alert('Please add at least one ingredient');
      return;
    }

    if (instructions.filter(i => i.trim()).length === 0) {
      alert('Please add at least one instruction step');
      return;
    }

    const recipeData = {
      userId: currentUser.uid,
      trainerId: userProfile?.role === 'trainer' ? currentUser.uid : userProfile?.trainerId,
      name: recipeName,
      description: recipeDescription,
      servings,
      prepTime: prepTime || null,
      cookTime: cookTime || null,
      category: category || 'Other',
      dietaryTags,
      ingredients: ingredients.map(ing => ({
        foodId: ing.foodId,
        name: ing.name,
        brand: ing.brand,
        quantity: ing.quantity,
        unit: ing.unit,
        calories: ing.calories,
        protein: ing.protein,
        carbs: ing.carbs,
        fat: ing.fat
      })),
      instructions: instructions.filter(i => i.trim()),
      totalNutrition,
      perServingNutrition,
      photoUrl: photoPreview, // In production, upload to Firebase Storage
      isPublic: false,
      sharedWith: [],
      createdAt: new Date(),
      updatedAt: new Date()
    };

    try {
      if (editingRecipe) {
        await updateRecipe(editingRecipe.id, recipeData);
        alert('Recipe updated successfully!');
      } else {
        await addRecipe(recipeData);
        alert('Recipe saved successfully!');
      }

      // Reset form
      resetForm();
      loadRecipes();
      setViewMode('library');
    } catch (error) {
      console.error('Error saving recipe:', error);
      alert('Error saving recipe. Please try again.');
    }
  };

  // Reset form
  const resetForm = () => {
    setRecipeName('');
    setRecipeDescription('');
    setServings(4);
    setPrepTime('');
    setCookTime('');
    setCategory('');
    setDietaryTags([]);
    setIngredients([]);
    setInstructions(['']);
    setRecipePhoto(null);
    setPhotoPreview(null);
    setEditingRecipe(null);
  };

  // Edit recipe
  const editRecipe = (recipe) => {
    setRecipeName(recipe.name);
    setRecipeDescription(recipe.description);
    setServings(recipe.servings);
    setPrepTime(recipe.prepTime || '');
    setCookTime(recipe.cookTime || '');
    setCategory(recipe.category);
    setDietaryTags(recipe.dietaryTags || []);
    setIngredients(recipe.ingredients.map(ing => ({ ...ing, id: Date.now() + Math.random() })));
    setInstructions(recipe.instructions);
    setPhotoPreview(recipe.photoUrl);
    setEditingRecipe(recipe);
    setViewMode('builder');
  };

  // Delete recipe
  const handleDeleteRecipe = async (recipeId) => {
    if (!window.confirm('Are you sure you want to delete this recipe?')) return;

    try {
      await deleteRecipe(recipeId);
      loadRecipes();
      alert('Recipe deleted successfully');
    } catch (error) {
      console.error('Error deleting recipe:', error);
      alert('Error deleting recipe. Please try again.');
    }
  };

  // Share recipe with clients
  const shareRecipe = async (recipeId) => {
    // TODO: Implement sharing functionality
    alert('Share functionality coming soon!');
  };

  const CATEGORIES = ['Breakfast', 'Lunch', 'Dinner', 'Snack', 'Dessert', 'Beverage', 'Other'];
  const DIETARY_TAGS = ['Vegetarian', 'Vegan', 'Gluten-Free', 'Dairy-Free', 'Keto', 'Low-Carb', 'High-Protein', 'Paleo'];

  return (
    <div className="recipe-builder">
      {/* Header */}
      <div className="builder-header">
        <div className="header-left">
          <h1><FaUtensils /> Recipe Builder</h1>
          <p>Create custom recipes with automatic nutrition calculation</p>
        </div>
        <div className="header-actions">
          <button
            className={`view-toggle-btn ${viewMode === 'builder' ? 'active' : ''}`}
            onClick={() => setViewMode('builder')}
          >
            <FaPlus /> New Recipe
          </button>
          <button
            className={`view-toggle-btn ${viewMode === 'library' ? 'active' : ''}`}
            onClick={() => setViewMode('library')}
          >
            <FaBook /> My Recipes ({recipes.length})
          </button>
        </div>
      </div>

      {viewMode === 'library' ? (
        /* Recipe Library View */
        <div className="recipe-library">
          {recipes.length === 0 ? (
            <div className="empty-library">
              <FaBook size={64} />
              <p>No recipes yet</p>
              <button className="btn-primary" onClick={() => setViewMode('builder')}>
                <FaPlus /> Create Your First Recipe
              </button>
            </div>
          ) : (
            <div className="recipes-grid">
              {recipes.map(recipe => (
                <div key={recipe.id} className="recipe-card">
                  {recipe.photoUrl && (
                    <div className="recipe-photo" style={{ backgroundImage: `url(${recipe.photoUrl})` }} />
                  )}
                  <div className="recipe-card-content">
                    <h3>{recipe.name}</h3>
                    <p className="recipe-description">{recipe.description}</p>

                    <div className="recipe-meta">
                      <span className="recipe-category">{recipe.category}</span>
                      <span className="recipe-servings">{recipe.servings} servings</span>
                    </div>

                    {recipe.dietaryTags && recipe.dietaryTags.length > 0 && (
                      <div className="recipe-tags">
                        {recipe.dietaryTags.map(tag => (
                          <span key={tag} className="dietary-tag">{tag}</span>
                        ))}
                      </div>
                    )}

                    <div className="recipe-nutrition">
                      <div className="nutrition-item">
                        <span className="value">{recipe.perServingNutrition.calories}</span>
                        <span className="label">Cal</span>
                      </div>
                      <div className="nutrition-item">
                        <span className="value">{recipe.perServingNutrition.protein}g</span>
                        <span className="label">Protein</span>
                      </div>
                      <div className="nutrition-item">
                        <span className="value">{recipe.perServingNutrition.carbs}g</span>
                        <span className="label">Carbs</span>
                      </div>
                      <div className="nutrition-item">
                        <span className="value">{recipe.perServingNutrition.fat}g</span>
                        <span className="label">Fat</span>
                      </div>
                    </div>

                    <div className="recipe-actions">
                      <button className="btn-icon" onClick={() => editRecipe(recipe)}>
                        <FaEdit /> Edit
                      </button>
                      <button className="btn-icon" onClick={() => shareRecipe(recipe.id)}>
                        <FaShare /> Share
                      </button>
                      <button className="btn-icon danger" onClick={() => handleDeleteRecipe(recipe.id)}>
                        <FaTrash /> Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        /* Recipe Builder View */
        <div className="builder-content">
          {/* Left Column - Recipe Details */}
          <div className="builder-main">
            {/* Basic Info */}
            <div className="builder-section">
              <h2>Basic Information</h2>

              <div className="form-group">
                <label>Recipe Name *</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="e.g., Grilled Chicken Salad"
                  value={recipeName}
                  onChange={(e) => setRecipeName(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  className="form-textarea"
                  placeholder="Brief description of your recipe..."
                  rows={3}
                  value={recipeDescription}
                  onChange={(e) => setRecipeDescription(e.target.value)}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Servings *</label>
                  <input
                    type="number"
                    className="form-input"
                    min="1"
                    value={servings}
                    onChange={(e) => setServings(parseInt(e.target.value) || 1)}
                  />
                </div>
                <div className="form-group">
                  <label>Prep Time</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="e.g., 15 mins"
                    value={prepTime}
                    onChange={(e) => setPrepTime(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Cook Time</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="e.g., 20 mins"
                    value={cookTime}
                    onChange={(e) => setCookTime(e.target.value)}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Category</label>
                <select
                  className="form-select"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                >
                  <option value="">Select a category</option>
                  {CATEGORIES.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Dietary Tags</label>
                <div className="tags-selector">
                  {DIETARY_TAGS.map(tag => (
                    <button
                      key={tag}
                      className={`tag-btn ${dietaryTags.includes(tag) ? 'selected' : ''}`}
                      onClick={() => toggleDietaryTag(tag)}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Recipe Photo</label>
                <div className="photo-upload">
                  <input
                    type="file"
                    id="photo-upload"
                    accept="image/*"
                    onChange={handlePhotoUpload}
                    style={{ display: 'none' }}
                  />
                  <label htmlFor="photo-upload" className="photo-upload-btn">
                    <FaCamera size={24} />
                    <span>Upload Photo</span>
                  </label>
                  {photoPreview && (
                    <div className="photo-preview">
                      <img src={photoPreview} alt="Recipe preview" />
                      <button className="remove-photo" onClick={() => { setRecipePhoto(null); setPhotoPreview(null); }}>
                        <FaTimes />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Ingredients */}
            <div className="builder-section">
              <h2>Ingredients *</h2>

              <div className="ingredient-search">
                <div className="search-input-wrapper">
                  <FaSearch className="search-icon" />
                  <input
                    type="text"
                    className="search-input"
                    placeholder="Search for ingredients..."
                    value={foodSearchTerm}
                    onChange={(e) => setFoodSearchTerm(e.target.value)}
                  />
                </div>

                {isSearching && (
                  <div className="searching">Searching...</div>
                )}

                {searchResults.length > 0 && (
                  <div className="search-results">
                    {searchResults.map(food => (
                      <div
                        key={food.id}
                        className="search-result-item"
                        onClick={() => addIngredient(food)}
                      >
                        <strong>{food.name}</strong>
                        {food.brand && <span className="brand">{food.brand}</span>}
                        <div className="result-macros">
                          <span>{food.calories} cal</span>
                          <span>P: {food.protein}g</span>
                          <span>C: {food.carbs}g</span>
                          <span>F: {food.fat}g</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="ingredients-list">
                {ingredients.length === 0 ? (
                  <div className="empty-ingredients">
                    <p>No ingredients added yet. Search and add ingredients above.</p>
                  </div>
                ) : (
                  ingredients.map(ingredient => (
                    <div key={ingredient.id} className="ingredient-item">
                      <div className="ingredient-info">
                        <strong>{ingredient.name}</strong>
                        {ingredient.brand && <span className="brand">{ingredient.brand}</span>}
                      </div>

                      <div className="ingredient-controls">
                        <div className="quantity-control">
                          <button
                            className="qty-btn"
                            onClick={() => updateIngredientQuantity(ingredient.id, ingredient.quantity - 0.5)}
                          >
                            <FaMinus />
                          </button>
                          <span className="quantity">
                            {ingredient.quantity} {ingredient.unit}
                          </span>
                          <button
                            className="qty-btn"
                            onClick={() => updateIngredientQuantity(ingredient.id, ingredient.quantity + 0.5)}
                          >
                            <FaPlus />
                          </button>
                        </div>

                        <button
                          className="remove-ingredient-btn"
                          onClick={() => removeIngredient(ingredient.id)}
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Instructions */}
            <div className="builder-section">
              <h2>Instructions *</h2>

              <div className="instructions-list">
                {instructions.map((instruction, index) => (
                  <div key={index} className="instruction-item">
                    <div className="instruction-number">{index + 1}</div>
                    <textarea
                      className="instruction-input"
                      placeholder={`Step ${index + 1} instructions...`}
                      rows={2}
                      value={instruction}
                      onChange={(e) => updateInstruction(index, e.target.value)}
                    />
                    {instructions.length > 1 && (
                      <button
                        className="remove-instruction-btn"
                        onClick={() => removeInstruction(index)}
                      >
                        <FaTimes />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              <button className="add-step-btn" onClick={addInstructionStep}>
                <FaPlus /> Add Step
              </button>
            </div>

            {/* Actions */}
            <div className="builder-actions">
              <button className="btn-secondary" onClick={resetForm}>
                <FaTimes /> Cancel
              </button>
              <button className="btn-primary" onClick={saveRecipe}>
                <FaSave /> {editingRecipe ? 'Update Recipe' : 'Save Recipe'}
              </button>
            </div>
          </div>

          {/* Right Column - Nutrition Summary */}
          <div className="nutrition-panel">
            <h2>Nutrition Information</h2>

            <div className="nutrition-summary">
              <div className="summary-section">
                <h3>Per Serving</h3>
                <div className="nutrition-grid">
                  <div className="nutrition-card">
                    <div className="value">{perServingNutrition.calories}</div>
                    <div className="label">Calories</div>
                  </div>
                  <div className="nutrition-card">
                    <div className="value">{perServingNutrition.protein}g</div>
                    <div className="label">Protein</div>
                  </div>
                  <div className="nutrition-card">
                    <div className="value">{perServingNutrition.carbs}g</div>
                    <div className="label">Carbs</div>
                  </div>
                  <div className="nutrition-card">
                    <div className="value">{perServingNutrition.fat}g</div>
                    <div className="label">Fat</div>
                  </div>
                </div>
              </div>

              <div className="summary-section">
                <h3>Total Recipe</h3>
                <div className="nutrition-grid">
                  <div className="nutrition-card total">
                    <div className="value">{Math.round(totalNutrition.calories)}</div>
                    <div className="label">Calories</div>
                  </div>
                  <div className="nutrition-card total">
                    <div className="value">{Math.round(totalNutrition.protein)}g</div>
                    <div className="label">Protein</div>
                  </div>
                  <div className="nutrition-card total">
                    <div className="value">{Math.round(totalNutrition.carbs)}g</div>
                    <div className="label">Carbs</div>
                  </div>
                  <div className="nutrition-card total">
                    <div className="value">{Math.round(totalNutrition.fat)}g</div>
                    <div className="label">Fat</div>
                  </div>
                </div>
              </div>

              <div className="summary-section">
                <h3>Recipe Stats</h3>
                <div className="stats-list">
                  <div className="stat-item">
                    <span className="stat-label">Servings:</span>
                    <span className="stat-value">{servings}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Ingredients:</span>
                    <span className="stat-value">{ingredients.length}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Steps:</span>
                    <span className="stat-value">{instructions.filter(i => i.trim()).length}</span>
                  </div>
                  {prepTime && (
                    <div className="stat-item">
                      <span className="stat-label">Prep Time:</span>
                      <span className="stat-value">{prepTime}</span>
                    </div>
                  )}
                  {cookTime && (
                    <div className="stat-item">
                      <span className="stat-label">Cook Time:</span>
                      <span className="stat-value">{cookTime}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecipeBuilder;
